import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Sparkles, Briefcase, ArrowRight, ChevronRight, ExternalLink, GraduationCap } from 'lucide-react';

export type PlansIntent = 'participar' | 'constelacao' | 'terapeuta';

const WHATSAPP_AVISOS_GRUPO =
  'https://chat.whatsapp.com/DsJxwW1i4IJ6pernEwmtQC';

const WORKSHOPS_URL = 'https://terapeutas.constelabrasil.com.br/workshops';

interface PathCardProps {
  to?: string;
  href?: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  accent: 'green' | 'purple' | 'teal' | 'gold';
  badge?: string;
  ctaLabel?: string;
}

const accentStyles = {
  green: {
    ring: 'hover:ring-[#7ED99C]/50',
    iconBg: 'bg-[#7ED99C]/15 text-[#3d8f5c]',
    gradient: 'from-[#7ED99C]/10 to-transparent',
    cta: 'text-[#3d8f5c] group-hover:text-[#2d6f45]',
  },
  purple: {
    ring: 'hover:ring-[#A56096]/50',
    iconBg: 'bg-[#A56096]/15 text-[#A56096]',
    gradient: 'from-[#A56096]/10 to-transparent',
    cta: 'text-[#A56096] group-hover:text-[#8a4f7f]',
  },
  teal: {
    ring: 'hover:ring-[#4A7C75]/50',
    iconBg: 'bg-[#4A7C75]/15 text-[#4A7C75]',
    gradient: 'from-[#4A7C75]/10 to-transparent',
    cta: 'text-[#4A7C75] group-hover:text-[#3a6560]',
  },
  gold: {
    ring: 'hover:ring-[#C17B4A]/50',
    iconBg: 'bg-[#C17B4A]/15 text-[#C17B4A]',
    gradient: 'from-[#C17B4A]/10 to-transparent',
    cta: 'text-[#C17B4A] group-hover:text-[#a3663c]',
  },
};

const cardClassName = (ring: string) =>
  `group relative flex min-h-0 flex-col rounded-2xl bg-white p-4 shadow-md ring-1 ring-black/5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg sm:rounded-3xl sm:p-5 ${ring} focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#4A7C75]`;

const PathCard: React.FC<PathCardProps> = ({
  to,
  href,
  icon,
  title,
  description,
  accent,
  badge,
  ctaLabel = 'Clique aqui',
}) => {
  const s = accentStyles[accent];
  const isExternal = Boolean(href);

  const inner = (
    <>
      <div
        className={`pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br ${s.gradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:rounded-3xl`}
        aria-hidden
      />
      <div className="relative flex flex-1 flex-col">
        {badge && (
          <span className="mb-2 inline-flex w-fit rounded-full bg-[#EAF4F2] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#4A7C75] sm:mb-3 sm:px-3 sm:py-1 sm:text-xs">
            {badge}
          </span>
        )}
        <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-xl sm:mb-4 sm:h-11 sm:w-11 sm:rounded-2xl ${s.iconBg}`}>
          {icon}
        </div>
        <h2 className="mb-2 text-[15px] font-bold leading-snug text-[#3A3157] sm:text-lg">{title}</h2>
        <p className="mb-3 line-clamp-4 flex-1 text-xs leading-relaxed text-[#4A7C75]/90 sm:mb-4 sm:line-clamp-none sm:text-sm">
          {description}
        </p>
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold sm:text-sm ${s.cta}`}>
          {ctaLabel}
          {isExternal ? (
            <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
          ) : (
            <ArrowRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-1" aria-hidden />
          )}
        </span>
      </div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cardClassName(s.ring)}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link to={to || '/planos'} className={cardClassName(s.ring)}>
      {inner}
    </Link>
  );
};

const PlansHub: React.FC = () => {
  return (
    <section className="mb-12 sm:mb-16" aria-labelledby="plans-hub-heading">
      <div className="mx-auto mb-6 max-w-3xl text-center sm:mb-10">
        <h1
          id="plans-hub-heading"
          className="text-xl font-bold leading-tight tracking-tight sm:text-2xl md:text-3xl lg:text-4xl"
          style={{ color: '#3A5859' }}
        >
          Encontre o que você busca na Constela Brasil
        </h1>
        <p className="mt-2 text-sm font-medium leading-snug text-[#A56096] sm:mt-3 sm:text-lg">
          Conheça as possibilidades disponíveis para consteladores, clientes e participantes.
        </p>
        <p className="mt-1.5 text-xs leading-relaxed text-[#4A7C75] sm:mt-2 sm:text-base">
          Acesse planos, constelações, profissionais, encontros, cursos, mentorias e experiências sistêmicas.
        </p>
      </div>

      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
        <PathCard
          to="/planos?secao=terapeuta"
          icon={<Briefcase className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />}
          title="Planos para Consteladores"
          description="Escolha entre horas avulsas ou torne-se um constelador homologado"
          accent="teal"
          badge="Profissional"
          ctaLabel="Clique aqui"
        />

        <PathCard
          to="/planos?secao=participante&intencao=constelacao"
          icon={<Sparkles className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />}
          title="Agende a sua Constelação"
          description="Conte com a nossa rede de Consteladores homologados pela plataforma. Todos com certificado de Formação e verificados pela plataforma."
          accent="purple"
          badge="Constelado"
          ctaLabel="Clique aqui para agendar"
        />

        <PathCard
          href={WORKSHOPS_URL}
          icon={<GraduationCap className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />}
          title="Cursos, mentorias e vivências"
          description="Encontre mentorias, workshops, cursos, formações, dinâmicas e experiências sistêmicas oferecidas por profissionais parceiros da Constela Brasil."
          accent="gold"
          badge="Consteladores e clientes"
          ctaLabel="Conheça aqui"
        />

        <PathCard
          href={WHATSAPP_AVISOS_GRUPO}
          icon={<Users className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />}
          title="Participe de uma constelação"
          description="Assista e participe das nossas constelações."
          accent="green"
          badge="Representante"
          ctaLabel="Entre no grupo de avisos"
        />
      </div>

      <div className="mx-auto mt-8 flex max-w-2xl flex-col items-center gap-3 sm:mt-12 sm:flex-row sm:justify-center sm:gap-4">
        <Link
          to="/quero-participar"
          className="inline-flex items-center gap-2 rounded-full border border-[#4A7C75]/30 bg-white px-6 py-3 text-sm font-medium text-[#4A7C75] shadow-sm transition hover:bg-[#EAF4F2]"
        >
          Ver salas abertas agora
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
        <Link
          to="/terapeutas"
          className="inline-flex items-center gap-2 rounded-full border border-[#A56096]/30 bg-white px-6 py-3 text-sm font-medium text-[#A56096] shadow-sm transition hover:bg-[#fdf8fc]"
        >
          Conhecer consteladores
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </section>
  );
};

export default PlansHub;
